import { Stack } from '@mantine/core';
import type { Filter } from '@repo/api-codegen';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import { EntityType } from '@repo/common/enums/entityType';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { IDocument } from '../../api';
import { useAuthUser } from '../../api';
import type { FetchModelInfiniteListHook } from '../../api/factories/types';
import {
	fetchCatalogList,
	useCatalogInfiniteList,
} from '../../api/hooks/resourceCatalog';
import { useTeamEmptyState } from '../../api/hooks/team/emptyStates';
import { useTemplateList } from '../../api/hooks/templates';
import { entityModalStore } from '../../components/EntityModal/store';
import { useCatalogFilter } from '../../components/Filter/useCatalogFilter';
import { TableV2 } from '../../components/TableV2';
import type { OnCellClickHandlerParams } from '../../components/TableV2/types';
import { useEntityBreadcrumbTeam } from '../../hooks/useEntityBreadcrumbs';
import { useHomepageTeam } from '../HomePage/hooks/useHomepageTeam';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { useActions, useColumns } from './TeamDocumentsPage.hooks';

const COLUMN_VISIBILITY = {
	catalogType: EntityType.document,
	catalogServerType: EntityType.document,
} as const;

const DEFAULT_CATALOG_FILTERS = {
	operands: [
		{
			operator: 'not',
			field: 'parent_id',
			operands: [],
		},
	],
} as { readonly operands: readonly Filter[] };

const DEFAULT_SEARCH_PARAMS_NESTING = {
	entity_type: EntityType.document,
	calculate_children_count: true,
} as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

const DEFAULT_SORT = {
	field: 'updated_at',
	order: 'desc',
} as const;

const QUICK_ACTIONS = [
	'actions::sidebar',
	'actions::verified',
	'actions::delete',
] as const;

const WITH_CUSTOM_PROPERTIES = [EntityType.document];

function TeamDocumentsPage() {
	const { classes } = useStyles();
	const { teamId } = useParams();
	const navigate = useNavigate();

	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();

	const { user, isAdminUser, isViewerOrGuestUser } = useAuthUser();
	const { isTeamEditorUser } = useHomepageTeam();

	const columns = useColumns();
	const actions = useActions();

	const { data: defaultTemplates } = useTemplateList({
		filters: {
			is_default: true,
			entity_type_template: EntityType.document,
		},
	});

	const handleCellClick = useCallback(
		({ column, record }: OnCellClickHandlerParams<IDocument>) => {
			if (column.navigate !== false) {
				if (teamId) setTeamBreadcrumb(teamId);
				navigate(`/docs/${record.id}`);
			}
		},
		[navigate, setTeamBreadcrumb, teamId]
	);

	const handleCreateDocumentClick = useCallback(async () => {
		await entityModalStore.quickCreateEntity(
			EntityType.document,
			'',
			[teamId ?? ''],
			{
				owners: [user.id],
			},
			navigate,
			defaultTemplates?.results?.[0]
		);
	}, [teamId, user.id, navigate, defaultTemplates]);

	const emptyState = useTeamEmptyState(teamId as string, 'document');

	const buttons: EmptyStateButtons = useMemo(
		() => [
			{
				label: 'Learn more',
				variant: 'default',
				onClick: () => {
					window.open('https://docs.secoda.com/features/documents', '_blank');
				},
			},
			{
				label: 'Create document',
				action: handleCreateDocumentClick,
				variant: 'primary',
				onClick: handleCreateDocumentClick,
				requiresUseButtonPermission: true,
			},
		],
		[handleCreateDocumentClick]
	);

	const defaultSearchParams = useMemo(
		() => ({
			team_id: teamId,
			entity_type: EntityType.document,
			calculate_children_count: true,
		}),
		[teamId]
	);

	const filterOptions = useCatalogFilter();

	if (emptyState?.is_empty) {
		return (
			<EmptyState
				heading="Welcome to Documents"
				body="Add the necessary context to your data resources and capture data knowledge with documents"
				illustrationName="documents"
				buttons={buttons as EmptyStateButtons}
				hasUseButtonPermission={isTeamEditorUser || isAdminUser}
			/>
		);
	}

	return (
		<Stack className={classes.wrapper}>
			<TableV2<IDocument>
				withCustomProperties={WITH_CUSTOM_PROPERTIES}
				pluralTypeString="documents"
				withInfiniteScroll
				withQuickActions={QUICK_ACTIONS}
				usePaginationList={
					useCatalogInfiniteList as FetchModelInfiniteListHook<IDocument>
				}
				fetchPaginationList={fetchCatalogList}
				withSearch
				withFilterOptions={filterOptions}
				withInteractiveHeader
				withCsvExport
				withServerCsvExport
				withCheckbox
				defaultSort={DEFAULT_SORT}
				withActions={actions}
				onCellClick={handleCellClick}
				columns={columns}
				columnVisibility={COLUMN_VISIBILITY}
				nestingFilter="parent_id"
				defaultRequiredCatalogFilters={DEFAULT_CATALOG_FILTERS}
				defaultRequiredSearchParamsNesting={DEFAULT_SEARCH_PARAMS_NESTING}
				defaultRequiredSearchParams={defaultSearchParams}
				usePaginationListOptions={PAGINATION_LIST_OPTIONS}
			/>
		</Stack>
	);
}

export default TeamDocumentsPage;
